import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {assertAndContinue} from 'Asserts';
import {UNASSIGNED_TASK_TEXT} from 'processes/ProcessConstants';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import {EntityWithTextAndSuspended} from 'ui-library/entity-input/EntityInputConstants';
import {EntityInputWithSearch} from 'ui-library/entity-input/EntityInputWithSearch';
import Modal from 'ui-library/modals/Modal';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';

import {ProcessTaskListingGraphQL} from '../../../../graphql-fragments/processes/__generated/ProcessTaskListingGraphQL';

interface ProcessTaskAssigneeEditorModalProps {
  onRequestSubmit: (assignee: EntityWithTextAndSuspended | null) => void;
  onRequestClose: () => void;
  items: Array<ProcessTaskListingGraphQL>;
}

function getAggregatedAssignee(items: Array<ProcessTaskListingGraphQL>): string {
  const existingAssigneeId = items[0].assignee?.id ?? null;
  const allSameAssignee = items.every((task) => task.assignee?.id ?? null == existingAssigneeId);
  return allSameAssignee ? (items[0].assignee?.handle ?? UNASSIGNED_TASK_TEXT) : 'Multiple assignees';
}

export default function ProcessTaskAssigneeEditorModal(
  props: ProcessTaskAssigneeEditorModalProps
): JSX.Element {
  assertAndContinue(
    props.items.length > 0,
    'ProcessTaskAssigneeEditorModal requires at least one selected item'
  );
  const [assignee, setAssignee] = useState<EntityWithTextAndSuspended | null>(null);
  return (
    <Modal
      title="Bulk update assignee"
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        props.onRequestSubmit(assignee);
      }}
      buttons={[
        {
          text: 'Update',
          submit: true,
          dataTestAttrs: {component: 'ProcessTaskAssigneeEditorModal', key: ModalButtonKey.UPDATE},
        },
      ]}
    >
      <div className={css(styles.container)}>
        <span className={css(styles.field)}>
          <span className={css(styles.label)}>Current Value:</span>
          <span className={css(styles.current)}>
            <div data-test="ProcessTaskAssigneeEditor-assigneeHandle" className="u-ellipsis-text">
              {getAggregatedAssignee(props.items)}
            </div>
          </span>
        </span>
        <span className={css(styles.field)}>
          <span className={css(styles.label)}>New Value:</span>
          <EntityInputWithSearch
            value={assignee ? [assignee] : []}
            placeholder="Leave blank to unassign"
            size={TagsInputSize.SMALL}
            onChange={(assignees: Array<EntityWithTextAndSuspended>) => {
              setAssignee(assignees[0]);
            }}
            allowEmails={true}
            allowTeams={false}
            allowOrganizations={false}
            shouldAllowMultiple={false}
            enableNewEntityInput={true}
            emptyText="No results found"
          />
        </span>
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
  },
  field: {
    flexGrow: 1,
  },
  label: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  current: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '8px',
  },
});
